import { Assert } from "../../Assert";
import { DataSource } from "../../Data/DataSource";
import { PropertyPermissionEvaluator } from "../../Evaluation/PropertyPermissionEvaluator";
import { PartialNullable } from "../../Extensions";
import { ObjectHelper } from "../../Helpers/ObjectHelper";
import { StringHelper } from "../../Helpers/StringHelper";
import { IJNativeEvent, JNative, JNativeStatic } from "../../JNative";
import { PropertyChange } from "../../PropertyChange";
import { Property, PropertyBoolean, PropertyObject, PropertyString } from "../../RadObject";
import { ClientAwareCategoryAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareCategoryAttribute";
import { ClientAwareDisplayNameAttribute } from "../../Server/DotNet/SeriouslyRAD.Framework/ClientAwareDisplayNameAttribute";
import { IEvaluator } from "../../Server/DotNet/SeriouslyRAD.Framework/Evaluation/IEvaluator";
import { BuiltInTypes } from "../../Server/DotNet/System/BuiltInTypes";
import { IDetailsPage } from "../Pages/DetailsPage";
import { IPage } from "../Pages/Page";
import { IControl } from "./Control";
import { ControlRef } from "./ControlRef";
import { IInputControl, InputControl, InputControlPropertyValues } from "./InputControl";
import { Label } from "./Label";
import { Text } from "./Text";
import { TextBox } from "./TextBox";
import { ValidationPanel } from "./ValidationPanel";
import { NAMESPACE } from "./_Namespace";

import "./PropertyEditor.scss";

export type PropertyEditorPropertyValues =
    InputControlPropertyValues
    & {
        createEditorControl: () => InputControl;
        createEditorControlProperties: () => any;
        createViewControl: () => InputControl;
        createViewControlProperties: () => any;
        editableEvaluator: IEvaluator;
        editMode: boolean;
        editorControl: IInputControl;
        editorControlProperties: any;
        helpText: string;
        hideLabel: boolean;
        labelColon: boolean;
        propertyDisplayName: string;
        relevantEvaluator: IEvaluator;
        showInlineValidationMessages: boolean;
        validationErrors: any[];
        validationMessages: any[];
        viewControl: InputControl;
        viewControlProperties: any;
    };

export type PropertyEditorProperties = {
    [name in keyof PropertyEditorPropertyValues]: Property<PropertyEditorPropertyValues[name]>
};

export interface IPropertyEditor extends IInputControl, PropertyEditorProperties
{
}

export class PropertyEditor extends InputControl implements IPropertyEditor
{
    // #region Static

    public static override classNamespace = NAMESPACE + "PropertyEditor";
    public static override className = "PropertyEditor";

    // #region Overrides

    public static override createPropertyDefaults(): PartialNullable<PropertyEditorPropertyValues>
    {
        return {
            ...super.createPropertyDefaults(),
            ...{
                dataSourceControlSelector: ".rad-control-page",
                editMode: false,
                hideLabel: false,
                labelColon: true,
                mandatory: false,
                propertyDisplayName: "Property",
                showInlineValidationMessages: false,
                validationMessages: null,
            }
        };
    }

    // #endregion

    // #endregion

    // #region Instance

    // #region Global Variables

    protected $$editElement?: JNative<HTMLElement>;
    protected $$viewElement?: JNative<HTMLElement>;
    protected labelControl?: Label;
    protected helpTextControl?: Text;
    protected processControlValueChanged?: boolean;
    protected validationPanel?: ValidationPanel;

    // #endregion

    // #region Properties

    public createEditorControl!: Property<() => InputControl>;
    public createEditorControlProperties!: Property<() => any>;
    public createViewControl!: Property<() => InputControl>;
    public createViewControlProperties!: Property<() => any>;
    public editableEvaluator!: Property<IEvaluator>;
    public editMode!: PropertyBoolean;
    public editorControl!: Property<IInputControl>;
    public editorControlProperties!: PropertyObject;
    public helpText!: PropertyString;
    public hideLabel!: PropertyBoolean;
    public labelColon!: PropertyBoolean;
    public propertyDisplayName!: PropertyString;
    public relevantEvaluator!: Property<IEvaluator>;
    public showInlineValidationMessages!: PropertyBoolean;
    public validationErrors!: Property<any[]>;
    public validationMessages!: Property<string[]>;
    public viewControl!: Property<InputControl>;
    public viewControlProperties!: PropertyObject;

    // #endregion

    // #region Constructor

    constructor(propertyValues?: PartialNullable<PropertyEditorPropertyValues>)
    {
        super(propertyValues);

        this.addCssClass("rad-control-property-editor field");
        this.processControlValueChanged = true;

        // Trigger Property Changes
        this.editMode.triggerChange();
        this.editorControl.triggerChange();
        this.helpText.triggerChange();
        this.hideLabel.triggerChange();
        this.propertyDisplayName.triggerChange();
        this.relevantEvaluator.triggerChange();
        this.showInlineValidationMessages.triggerChange();
        this.validationErrors.triggerChange();
        this.validationMessages.triggerChange();
    }

    // #endregion

    // #region Public Methods

    public async loadDataAsync(): Promise<void>
    {
        const
            editorControl = this.editorControl.get() as any | null;

        if (editorControl?.loadDataAsync)
        {
            await editorControl.loadDataAsync();
            this.refreshFromDataSource();
        }
    }

    // #endregion

    // #region Protected Methods

    protected _controlValueGet()
    {
        const
            editorControl = this.editorControl.get();

        Assert.isNotNullOrUndefined(editorControl, "editorControl");

        return editorControl.value.get();
    }
    protected _controlValueSet(value: any)
    {
        const
            editorControl = this.editorControl.get();

        Assert.isNotNullOrUndefined(editorControl, "editorControl");

        this.processControlValueChanged = false;
        editorControl.value.set(value);
        this.processControlValueChanged = true;
    }
    protected _editModeChanged(change: PropertyChange<boolean>)
    {
        const
            editable = this.editableEvaluator.get()?.evaluate() ?? true,
            editMode = change.currentValue,
            readOnly = this.readOnly.get();

        if (editable && editMode && !readOnly)
        {
            this.labelControl?.associatedControlRef.set(new ControlRef(this.editorControl.get()));
            this.addCssClass("rad-state-edit");
        }
        else
        {
            this.labelControl?.associatedControlRef.set(null);
            this.removeCssClass("rad-state-edit");
        }
    }
    protected _editorControlChanged(change: PropertyChange<IInputControl>)
    {
        if (change.originalValue)
            this._editorControlEventsRemove(change.originalValue);

        if (change.currentValue)
            this._editorControlEventsAdd(change.currentValue);

        if (!this.labelControl)
            this.labelControl = new Label({ associatedControlRef: new ControlRef(change.currentValue), colon: this.labelColon.get() });
        else
            this.labelControl.associatedControlRef.set(new ControlRef(change.currentValue));
    }
    protected _editorControlEventsAdd(editorControl: IControl)
    {
        if (!editorControl)
            return;

        editorControl.on("valueChanged", { self: this }, this.onEditorControlValueChanged);
        editorControl.on("textChanged", { self: this }, this.onEditorControlTextChanged);

        if (editorControl._getFocusElement)
        {
            const
                $$focus = editorControl._getFocusElement();

            $$focus?.on("blur", { self: this }, this.onEditorControlBlur);
            $$focus?.on("focus", { self: this }, this.onEditorControlFocus);
        }
    }
    protected _editorControlEventsRemove(editorControl: IControl)
    {
        if (!editorControl)
            return;

        editorControl.off("valueChanged", { self: this }, this.onEditorControlValueChanged);
        editorControl.off("textChanged", { self: this }, this.onEditorControlTextChanged);

        if (editorControl._getFocusElement)
        {
            const
                $$focus = editorControl._getFocusElement();

            $$focus?.off("blur", { self: this }, this.onEditorControlBlur);
            $$focus?.off("focus", { self: this }, this.onEditorControlFocus);
        }
    }
    protected _editorControlPropertiesChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }
    protected _helpTextChanged(change: PropertyChange<string>)
    {
        if (!this.helpTextControl)
            return;

        this.helpTextControl.text.set(change.currentValue);
        this.helpTextControl.visible.set(!!change.currentValue);
    }
    protected _hideLabelChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue)
            this.addCssClass("rad-state-label-hidden");
        else
            this.removeCssClass("rad-state-label-hidden");
    }
    protected _labelColonChanged(change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }
    protected _maximumLengthChanged(change: PropertyChange<number>)
    {
        const
            editorControl = this.editorControl.get();

        if (editorControl instanceof TextBox)
            editorControl.maximumLength.set(change.currentValue);
    }
    protected _propertyDisplayNameChanged(change: PropertyChange<string>)
    {
        this.labelControl?.text.set(change.currentValue);
    }
    protected _registerSaveRequests(data: any)
    {
        /* Intentionally empty */
    }
    protected _relevantEvaluatorChanged(change: PropertyChange<IEvaluator>)
    {
        if (this.relevantEvaluator)
        {
            this.relevantEvaluator.get()?.off("valueChanged", { self: this }, this.onRelevantEvaluatorValueChanged);
            this.relevantEvaluator.get()?.context.set(null);
        }

        if (this.editableEvaluator)
        {
            this.editableEvaluator.get()?.off("valueChanged", { self: this }, this.onEditableEvaluatorValueChanged);
            this.editableEvaluator.get()?.context.set(null);
        }

        if (change.currentValue)
        {
            this.relevantEvaluator.set(change.currentValue);

            this.relevantEvaluator.get()?.on("valueChanged", { self: this }, this.onRelevantEvaluatorValueChanged);
            this.relevantEvaluator.get()?.context.set(this);

            this.editableEvaluator.set(new PropertyPermissionEvaluator({ permission: 4 }));
            this.editableEvaluator.get()?.on("valueChanged", { self: this }, this.onEditableEvaluatorValueChanged);
            this.editableEvaluator.get()?.context.set(this);
        }
        else
            this.relevantEvaluator.set(null);

        this.visibleEvaluator.set(this.relevantEvaluator.get());
    }
    protected _setEditorControlDataSource()
    {
        const
            editorControl = this.editorControl.get();

        Assert.isNotNullOrUndefined(editorControl, "editorControl");

        editorControl.dataSource.set(this.dataSource.get());
    }
    protected _setEditorControlDataSourceProperty()
    {
        const
            editorControl = this.editorControl.get();

        Assert.isNotNullOrUndefined(editorControl, "editorControl");

        editorControl.dataSourceProperty.set(this.dataSourceProperty.get());
    }
    protected _setEditorControlDataSourcePropertyType()
    {
        const
            editorControl = this.editorControl.get();

        Assert.isNotNullOrUndefined(editorControl, "editorControl");

        editorControl.dataSourcePropertyType.set(this.dataSourcePropertyType.get());
    }
    protected _showInlineValidationMessagesChanged(change: PropertyChange<boolean>)
    {
        if (change.currentValue && !this.validationPanel)
            this.validationPanel = new ValidationPanel({ shouldScrollToTop: false, titleTextWhenValidationError: StringHelper.empty }).appendTo(this.$$element.get());
    }
    protected _updateValidationErrorsFromDataSource()
    {
        const
            dataSource = this.dataSource.get(),
            dataSourceProperty = this.dataSourceProperty.get(),
            dataSourceValidationErrors = dataSource ? dataSource.validationErrors.get() : null;

        this.validationErrors.set(
            dataSourceValidationErrors && dataSourceProperty
                ? dataSourceValidationErrors.filter((item: any) => item.propertyName && StringHelper.toLowerCamelCasePath(item.propertyName) === dataSourceProperty)
                : null
        );
    }
    protected _updateViewElement()
    {
        if (this.$$viewElement && !this.viewControl.get())
        {
            const
                editorControl = this.editorControl.get();

            Assert.isNotNullOrUndefined(editorControl, "editorControl");

            this.$$viewElement.empty();
            this.$$viewElement.append(JNativeStatic.create$$({ nodeName: "p", text: editorControl.text.get() ?? undefined }));
        }
    }
    protected _validate()
    {
        const
            validationErrors = [],
            value = this.value.get(),
            valueType = ObjectHelper.typeOf(value),
            hasValue =
                valueType === "string"
                    ? !StringHelper.isNullOrWhitespace(value)
                    : !ObjectHelper.isNullOrUndefined(value);

        if (this.isMandatoryGet() && !hasValue)
        {
            validationErrors.push({
                errorMessage: StringHelper.format("{0} is required.", this.propertyDisplayName.get())
            });
        }

        if (validationErrors.length)
            this.addCssClass("rad-state-error");
        else
            this.removeCssClass("rad-state-error");

        return validationErrors;
    }
    protected _validationErrorsChanged(change: PropertyChange<any[]>)
    {
        this.errorState.set(!!(change.currentValue?.length));
    }
    protected _validationMessagesChanged(change: PropertyChange<string[]>)
    {
        if (this.validationPanel)
            this.validationPanel.setErrorMessages(change.currentValue);
    }
    protected _viewControlChanged(change: PropertyChange<InputControl>)
    {
        /* Intentionally empty */
    }
    protected _viewControlPropertiesChanged(change: PropertyChange<any>)
    {
        /* Intentionally empty */
    }

    protected onEditableEvaluatorValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.editMode.triggerChange()
    }

    // #endregion

    // #region Overrides

    protected override __initializeProperties(propertyValues: PropertyEditorPropertyValues)
    {
        super.__initializeProperties(propertyValues);

        this.createEditorControl = new Property<() => InputControl>(this, "System.Object, System.Private.CoreLib", propertyValues.createEditorControl);
        this.createEditorControlProperties = new Property<() => any>(this, "System.Object, System.Private.CoreLib", propertyValues.createEditorControlProperties);
        this.createViewControl = new Property<() => InputControl>(this, "System.Object, System.Private.CoreLib", propertyValues.createViewControl);
        this.createViewControlProperties = new Property<() => any>(this, "System.Object, System.Private.CoreLib", propertyValues.createViewControlProperties);
        this.editableEvaluator = new Property<IEvaluator>(this, "System.Object, System.Private.CoreLib", propertyValues.editableEvaluator);
        this.editMode = new PropertyBoolean(this, propertyValues.editMode, this._editModeChanged);
        this.editorControl = new Property<IInputControl>(this, "System.Object, System.Private.CoreLib", propertyValues.editorControl, this._editorControlChanged);
        this.editorControlProperties = new PropertyObject(this, propertyValues.editorControlProperties, this._editorControlPropertiesChanged);
        this.helpText = new PropertyString({
            context: this,
            initialValue: propertyValues.helpText,
            changeHandler: this._helpTextChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Appearance" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Help Text" }),
            ],
        });
        this.hideLabel = new PropertyBoolean(this, propertyValues.hideLabel, this._hideLabelChanged);
        this.labelColon = new PropertyBoolean(this, propertyValues.labelColon, this._labelColonChanged);
        this.propertyDisplayName = new PropertyString({
            context: this,
            initialValue: propertyValues.propertyDisplayName,
            changeHandler: this._propertyDisplayNameChanged,
            attributes: [
                new ClientAwareCategoryAttribute({ category: "Data" }),
                new ClientAwareDisplayNameAttribute({ displayName: "Property Display Name" }),
            ],
        });
        this.relevantEvaluator = new Property<IEvaluator>(this, "System.Object, System.Private.CoreLib", propertyValues.relevantEvaluator, this._relevantEvaluatorChanged);
        this.showInlineValidationMessages = new PropertyBoolean(this, propertyValues.showInlineValidationMessages, this._showInlineValidationMessagesChanged);
        this.validationErrors = new Property<any[]>(this, "System.Object, System.Private.CoreLib", propertyValues.validationErrors, this._validationErrorsChanged);
        this.validationMessages = new Property<string[]>(this, "System.Object, System.Private.CoreLib", propertyValues.validationMessages, this._validationMessagesChanged);
        this.viewControl = new Property<InputControl>(this, "System.Object, System.Private.CoreLib", propertyValues.viewControl, this._viewControlChanged);
        this.viewControlProperties = new PropertyObject(this, propertyValues.viewControlProperties, this._viewControlPropertiesChanged);

        this.editorControl.overrideGet(next =>
        {
            let
                editorControl = next();

            if (!editorControl)
            {
                const
                    createEditorControl = this.createEditorControl.get();

                if (createEditorControl)
                {
                    editorControl = createEditorControl();
                    this.editorControl.set(editorControl);
                }
            }

            return editorControl;
        });
        this.editorControlProperties.overrideGet(next =>
        {
            let
                editorControlProperties = next();

            if (!editorControlProperties)
            {
                const
                    createEditorControlProperties = this.createEditorControlProperties.get();

                if (createEditorControlProperties)
                {
                    editorControlProperties = createEditorControlProperties();
                    this.editorControlProperties.set(editorControlProperties);
                }
            }

            return editorControlProperties;
        });
        this.value.overrideSet((value, next) =>
        {
            this._controlValueSet(value);
            value = this._controlValueGet();
            next(value);
            this.updateDataSource();
        });
        this.viewControl.overrideGet(next =>
        {
            let
                viewControl = next();

            if (!viewControl)
            {
                const
                    createViewControl = this.createViewControl.get();

                if (createViewControl)
                {
                    viewControl = createViewControl();
                    this.viewControl.set(viewControl);
                }
            }

            return viewControl;
        });
        this.viewControlProperties.overrideGet(next =>
        {
            let
                viewControlProperties = next();

            if (!viewControlProperties)
            {
                const
                    createViewControlProperties = this.createViewControlProperties.get();

                if (createViewControlProperties)
                {
                    viewControlProperties = createViewControlProperties();
                    this.viewControlProperties.set(viewControlProperties);
                }
            }

            return viewControlProperties;
        });
    }

    protected override _createElement()
    {
        const
            $$element = super._createElement(),
            viewControl = this.viewControl.get(),
            viewControlProperties = this.viewControlProperties.get(),
            editorControlProperties = this.editorControlProperties.get();

        let
            editorControl = this.editorControl.get();

        if (viewControl && viewControlProperties)
            viewControl.setProperties(viewControlProperties);

        Assert.isNotNullOrUndefined(editorControl, "editorControl");

        if (editorControlProperties)
            editorControl.setProperties(editorControlProperties);

        this.labelControl = new Label({ associatedControlRef: new ControlRef(editorControl), colon: this.labelColon.get() });
        this.labelControl.appendTo($$element);
        this.$$viewElement = JNativeStatic.create$$("div", "rad-field-view").appendTo($$element);
        this.$$editElement = JNativeStatic.create$$("div", "rad-field-edit").appendTo($$element);
        this.helpTextControl = new Text({ cssClass: "rad-control-help-text", visible: false });
        this.helpTextControl.appendTo(this.$$editElement);
        JNativeStatic.create$$("div", "rad-clear-float").appendTo($$element);

        // Make sure the property editor value is the same as the editorControl default.
        this.value.set(editorControl.value.get(), false);

        if (viewControl)
            viewControl.appendTo(this.$$viewElement);

        editorControl.appendTo(this.$$editElement);

        return $$element;
    }
    protected override _dataSourceChanged(change: PropertyChange<DataSource>)
    {
        super._dataSourceChanged(change);

        const
            viewControl = this.viewControl.get();

        if (viewControl)
            viewControl.dataSource.set(change.currentValue);

        this._setEditorControlDataSource();
        this._updateValidationErrorsFromDataSource();
    }
    protected override _dataSourcePropertyChanged(change: PropertyChange<string>)
    {
        const
            viewControl = this.viewControl.get();

        if (viewControl)
            viewControl.dataSourceProperty.set(change.currentValue);

        this._setEditorControlDataSourceProperty();

        super._dataSourcePropertyChanged(change);
    }
    protected override _dataSourcePropertyTypeChanged(change: PropertyChange<BuiltInTypes>)
    {
        const
            viewControl = this.viewControl.get();

        if (viewControl)
            viewControl.dataSourcePropertyType.set(change.currentValue);

        this._setEditorControlDataSourcePropertyType();

        super._dataSourcePropertyTypeChanged(change);
    }
    protected override _dispose()
    {
        this.labelControl?.remove();
        this.viewControl.get()?.remove();
        this.editorControl.get()?.remove();

        super._dispose();
    }
    protected override _enabledChanged(change: PropertyChange<boolean>)
    {
        super._enabledChanged(change);

        const
            editorControl = this.editorControl.get();

        Assert.isNotNullOrUndefined(editorControl, "editorControl");

        editorControl.enabled.set(change.currentValue);
    }
    protected override _errorStateChanged(change: PropertyChange<boolean>)
    {
        const
            validationMessages = this.validationMessages.get(),
            hasValidationMessages = Array.isArray(validationMessages) && validationMessages.length,
            showInlineValidationMessages = this.showInlineValidationMessages.get();

        super._errorStateChanged(change);

        const
            editorControl = this.editorControl.get();

        Assert.isNotNullOrUndefined(editorControl, "editorControl");

        editorControl.errorState.set(change.currentValue);

        if (change.currentValue && showInlineValidationMessages && hasValidationMessages && this.validationPanel)
        {
            this.validationPanel.setErrorMessages(validationMessages);
            this.validationPanel.show();
        }
        else if (this.validationPanel)
            this.validationPanel.hide();
    }
    protected override _mandatoryChanged(change: PropertyChange<boolean>)
    {
        super._mandatoryChanged(change);

        const
            editorControl = this.editorControl.get();

        Assert.isNotNullOrUndefined(editorControl, "editorControl");

        editorControl.mandatory.set(this.mandatory.get());
    }
    protected override _mandatoryEvaluatorChanged(change: PropertyChange<IEvaluator>)
    {
        super._mandatoryEvaluatorChanged(change);

        const
            editorControl = this.editorControl.get();

        Assert.isNotNullOrUndefined(editorControl, "editorControl");

        editorControl.mandatoryEvaluator.set(this.mandatoryEvaluator.get());
    }
    protected override _pageChanged(change: PropertyChange<IPage>)
    {
        super._pageChanged(change);

        if (change.originalValue)
        {
            change.originalValue.off("creatingSaveBatch", { self: this }, this.onPageCreatingSaveBatch);
            change.originalValue.off("editModeChanged", { self: this }, this.onPageEditModeChanged);
        }

        if (change.currentValue)
        {
            change.currentValue.on("creatingSaveBatch", { self: this }, this.onPageCreatingSaveBatch);
            change.currentValue.on("editModeChanged", { self: this }, this.onPageEditModeChanged);

            if (ObjectHelper.isAssignableTo<IDetailsPage>(change.currentValue, "@seriously-rad/seriouslyrad-framework-core/Ui/Pages/DetailsPage.DetailsPage"))
                this.editMode.set(change.currentValue.editMode.get());
        }
    }
    protected override _readOnlyChanged(change: PropertyChange<boolean>)
    {
        super._readOnlyChanged(change);

        const
            editorControl = this.editorControl.get();

        if (editorControl instanceof InputControl)
            editorControl.readOnly.set(change.currentValue);
    }
    protected override _valueChanged(change: PropertyChange<any>)
    {
        super._valueChanged(change);
        this._updateViewElement();

        if (change.currentValue)
            this.removeCssClass("rad-state-empty");
        else
            this.addCssClass("rad-state-empty");
    }

    protected override onDataSourceValidationErrorsChanged(evt: IJNativeEvent, change: any)
    {
        const
            self = evt.data.self as this;

        self._updateValidationErrorsFromDataSource();
    }
    public override remove()
    {
        this.removeControls([
            this.labelControl,
            this.editorControl.get(),
            this.viewControl.get()
        ]);

        super.remove();
    }

    // #endregion

    // #region Event Handlers

    protected onEditorControlBlur(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.removeCssClass("rad-state-focussed");
    }
    protected onEditorControlFocus(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self.addCssClass("rad-state-focussed");
    }
    protected onEditorControlTextChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        self._updateViewElement();
    }
    protected onEditorControlValueChanged(evt: IJNativeEvent)
    {
        const
            self = evt.data.self as this;

        if (!self.processControlValueChanged)
            return;

        self.value.set(self._controlValueGet());
        self._updateViewElement();
        self.updateDataSource();
        self.errorState.set(false);
    }
    protected onPageCreatingSaveBatch(evt: IJNativeEvent, eventData: any)
    {
        const
            self = evt.data.self as this;

        self._registerSaveRequests(eventData);
    }
    protected onPageEditModeChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        const
            self = evt.data.self as this;

        self.editMode.set(change.currentValue);
    }
    protected onRelevantEvaluatorValueChanged(evt: IJNativeEvent, change: PropertyChange<boolean>)
    {
        /* Intentionally empty */
    }

    // #endregion

    // #endregion
}